import { useEffect, useState } from 'react';
import { instanceAuthEnabled, lastKnownInstanceAuthEnabled } from './instance-auth-flag';
import { ensureInstance } from './instance-auth';
import { redirectToLogin } from './auth-redirect';
import { logLoginIfPending } from './appsflyer';

/**
 * App-load bootstrap for the signed-instance auth path — the giza-style "try to
 * get the instance on load, else sign in". Returns `gating`: while true, `_app`
 * covers the app with a loader so nothing is shown/used before auth is resolved.
 *
 * Flow (client only, off the render path):
 * - flag OFF          → never gates (returns `gating=false`); no change for users
 *                       the toggle doesn't target.
 * - flag ON + `ok`    → gate while acquiring, then reveal.
 * - flag ON + `none`  → reveal (authenticated, no metasite yet — pre-onboarding).
 * - flag ON + `unauthenticated` → redirect to sign-in (stays gated while navigating).
 *
 * SSR-safe: `gating` starts `false` (server + first client render agree — no
 * loader in the SSR HTML, no hydration mismatch). On mount it seeds from the
 * last-known flag so a returning flagged user is covered promptly, then resolves.
 */
export function useInstanceAuthBootstrap(): { gating: boolean } {
  const [gating, setGating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // af_login, once, if we've just come back from sign-in. Deliberately BEFORE the
    // instance-auth work below: it is about the login that already happened, not about
    // this toggle, and every path out of the async block below either returns early or
    // navigates away — so hanging it off them reported af_login for toggle-targeted
    // users only, and never for anyone else.
    logLoginIfPending();
    // Cover promptly for a returning flagged user (avoids a flash of the app).
    if (lastKnownInstanceAuthEnabled() === true) setGating(true);

    void (async () => {
      const enabled = await instanceAuthEnabled();
      if (cancelled) return;
      if (!enabled) {
        setGating(false);
        return;
      }
      setGating(true);
      const result = await ensureInstance();
      if (cancelled) return;
      if (result.status === 'unauthenticated') {
        redirectToLogin(); // navigating away — keep the loader up
        return;
      }
      setGating(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { gating };
}
